import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import { Check } from "lucide-react";
import React, { useState } from "react";
import { Separator } from "./ui/separator";
import Step2 from "./RegistrationStep2";
import Step3 from "./RegistrationStep3";
import Step4 from "./RegistrationStep4";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import { step2Schema, step3Schema, step4Schema } from "./schemas";
import type { Brand } from "./AppSidebar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { authAPI, brandsAPI, promptsAPI } from "@/services/api";
import type { AxiosError } from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useDataStore, type ClusterWithPrompt } from "@/store/useDataStore";
import { MAX_CLUSTERS_NUMBER } from "./Register";

type StepSchemas = [typeof step2Schema, typeof step3Schema, typeof step4Schema];

type StepData = [
  z.infer<typeof step2Schema>,
  z.infer<typeof step3Schema>,
  z.infer<typeof step4Schema>,
];

const steps = [
  {
    name: "Brand Setup",
    number: 1,
  },
  {
    name: "Clusters",
    number: 2,
  },
  {
    name: "Prompts",
    number: 3,
  },
];

const defaultPrompts: ClusterWithPrompt[] = [];
const MAX_PROMPTS_NUMBER = 16;

export const AddBrandDialog = ({
  setOpen,
  company,
  defaultBrand: _defaultBrand,
  setDropDownOpened,
  brands,
  setBrands,
  setIsCreationInProgress,
  setCurrentBrand,
}: {
  setOpen: (open: boolean) => void;
  defaultBrand: Brand;
  setDropDownOpened: (s: boolean) => void;
  brands: Brand[];
  setIsCreationInProgress: (p: boolean) => void;
  setBrands: (brands: Brand[]) => void;
  setCurrentBrand: (brand: Brand) => void;
  company: any;
}) => {
  const { t } = useLocale();
  const [step, setStep] = useState(0);
  const stepSchemas: StepSchemas = [step2Schema, step3Schema, step4Schema];
  const [loadingStep, setLoadingStep] = useState(false);
  const [error, setError] = useState("");
  const [openCancelConfirm, setOpenCancelConform] = useState(false);
  const [brand, setBrand] = useState<Brand | null>(null);
  const navigate = useNavigate();
  const { allClusters } = useDataStore();

  const resetDialog = () => {
    setOpen(false);
    setOpenCancelConform(false);
    setError("");
    setStep(0);
    setBrand(null);
  };

  const handleCancel = async () => {
    const draftBrandId = brand?.id || brand?.brandId;
    setLoadingStep(true);

    try {
      if (draftBrandId) {
        await brandsAPI.deleteBrand(draftBrandId);
        setBrands(
          brands.filter((item) => (item.id || item.brandId) !== draftBrandId),
        );
      }

      setIsCreationInProgress(false);
      resetDialog();
    } catch (err: unknown) {
      const axiosError = err as AxiosError<{ message: string }>;
      setError(
        axiosError.response?.data?.message ||
          "Couldn't cancel brand setup. Please try again.",
      );
    } finally {
      setLoadingStep(false);
    }
  };

  const onSubmit = async (data: any) => {
    const isValid = await methods.trigger();
    if (isValid) {
      setLoadingStep(true);
      try {
        await authAPI.setPrompts(data.clusters, brand?.id || brand?.brandId);
        toast.success(t("legacy.the.brands.has.been.added.p661qb"), {
          icon: (
            <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
              <Check className="w-4 h-4 text-black" />
            </div>
          ),
          position: "bottom-right",
        });
        setOpen(false);
        setDropDownOpened(false);
        navigate("/dashboard/overview");
        if (brand) {
          setCurrentBrand(brand);
        }
      } catch (err: unknown) {
        const error = err as AxiosError<{ message: string }>;
        setError(error.response?.data?.message || "Unknown error occurred");
      } finally {
        setLoadingStep(false);
        setIsCreationInProgress(false);
        setBrand(null);
      }
    }
  };

  const registerBrand = async (
    brandCountry: string,
    brandWebsite: string,
    brandName: string,
    currentFocus: string,
  ) => {
    const isValid = await methods.trigger();
    if (isValid) {
      setLoadingStep(true);
      try {
        if (!company || !brand) {
          const result = await brandsAPI.setupBrand({
            country: brandCountry,
            website: brandWebsite,
            name: brandName,
            currentFocus: currentFocus,
            companyId: company?.id || company?.companyId || null,
          });
          const currentBrand = {
            id: result.brand.id,
            brandId: result.brand.id,
            companyId: result.brand.companyId || company?.id || null,
            company: result.brand.company || company || null,
            country: result.brand.country,
            name: result.brand.name,
            url: result.brand.url,
            currentFocus: result.brand.currentFocus,
            activeProviders: result.brand.activeProviders,
            created_at: result.brand.created_at,
            createdAt: result.brand.createdAt,
            createdDuringSession: true,
          };
          setBrand(currentBrand || null);
          setBrands([
            ...brands,
            { ...currentBrand, createdDuringSession: false },
          ]);
        }
        setStep(1);
      } catch (err: unknown) {
        const error = err as AxiosError<{ message: string }>;
        setError(error.response?.data?.message || "Unknown error occurred");
      } finally {
        setLoadingStep(false);
      }
    }
  };

  const methods = useForm<StepData[typeof step]>({
    resolver: zodResolver(stepSchemas[step]),
    mode: "onTouched",
  });

  const { data: clusters = allClusters, isLoading: isClusterLoading } =
    useQuery({
      queryKey: ["generateClusters", brand?.id],
      queryFn: async () => {
        const result = await promptsAPI.generateClusters(
          brand?.id || "",
          MAX_CLUSTERS_NUMBER,
        );
        return result.clusters.length ? result.clusters : [];
      },
      staleTime: Infinity,
      enabled: !!brand?.id && !allClusters?.length && step === 1,
    });

  const selectedClusters = methods.watch("clusters") || [];
  const clustersKey = JSON.stringify(
    selectedClusters.map((cluster: { title: string }) => cluster.title),
  );

  const {
    data: clustersWithPrompts = defaultPrompts,
    isLoading: isPromptsLoading,
  } = useQuery({
    queryKey: ["generatePrompts", brand?.id, clustersKey],
    queryFn: async () => {
      if (!selectedClusters.length) return [];
      const result = await promptsAPI.generateClusterPrompts(
        brand?.id || "",
        selectedClusters,
        MAX_PROMPTS_NUMBER,
      );
      return result.clusters.map((cluster) => ({
        title: cluster.title,
        clusterId: cluster.clusterId,
        prompts: cluster.prompts.map((prompt, index) => ({
          text: prompt.text,
          country: String(prompt.country || "")
            .trim()
            .toUpperCase(),
          selected: index === 0 || index === 1,
          isCustom: false,
        })),
      }));
    },
    staleTime: Infinity,
    enabled: !!brand?.id && selectedClusters.length > 0 && step === 2,
  });

  const registerClusters = async (clusters: { title: string }[]) => {
    const isValid = await methods.trigger();
    console.log(clusters);
    if (isValid) {
      setLoadingStep(true);
      try {
        // const result = await authAPI.setupBrand({
        //     "country": brandCountry,
        //     "website": brandWebsite,
        //     "name": brandName
        // });
        // setBrandId(result.brand.id || "")
        // setBrand(result.brand || null)
        setStep(2);
      } catch (err: unknown) {
        const error = err as AxiosError<{ message: string }>;
        setError(error.response?.data?.message || "Unknown error occurred");
      } finally {
        setLoadingStep(false);
      }
    }
  };

  return (
    <>
      <div className="flex justify-between text-sm">
        {steps.map((st) => {
          return (
            <React.Fragment key={st.number}>
              <div
                className={`flex flex-col items-center text-[#393B40] px-3 md:px-0 ${step + 1 >= st.number && "text-[#FF5E00]"}  `}
              >
                <div
                  className={`flex justify-center items-center w-[32px] h-[32px] border rounded-full border-[1.5px]  ${step + 1 >= st.number && "border-[#FF5E00] font-medium"} ${step + 1 > st.number && "bg-[#FF5E00]"}`}
                >
                  {step + 1 > st.number ? (
                    <Check className="text-white" />
                  ) : (
                    `0${st.number}`
                  )}
                </div>
                <div
                  className={`text-[#737373] font-medium text-sm hidden md:block ${step + 1 >= st.number && "text-[#FF5E00]"} `}
                >
                  {st.name}
                </div>
              </div>
              {st.number <= 2 && (
                <div className="grow relative translate-y-1/3">
                  <Separator
                    className={`${st.number < step + 1 && "bg-[#FF5E00]"} `}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      <Separator className="my-3"></Separator>
      <FormProvider {...methods}>
        <form
          onSubmit={(event) => {
            if (step !== 2) {
              event.preventDefault();
              return;
            }
            void methods.handleSubmit(onSubmit)(event);
          }}
        >
          {step === 0 && (
            <Step2
              prefilled={false}
              additionalAction={() => {
                void handleCancel();
              }}
              isScrollable={true}
              loadingStep={loadingStep}
              registerBrand={registerBrand}
              error={error}
              company={company}
              brand={null}
            />
          )}
          {step === 1 && (
            <Step3
              brandId={brand?.id || ""}
              loadingStep={loadingStep}
              error={error}
              registerClusters={registerClusters}
              company={brand || company}
              clusters={clusters}
              isLoading={isClusterLoading}
              additionalAction={() => {
                setOpenCancelConform(true);
              }}
            />
          )}
          {step === 2 && (
            <Step4
              isBrandAdd={true}
              additionalAction={() => {
                setOpenCancelConform(true);
              }}
              clustersWithPrompts={clustersWithPrompts}
              isLoading={isPromptsLoading}
              smallerLoading={true}
              brandId={brand?.id || ""}
              loadingStep={loadingStep}
              error={error}
              company={brand || company}
            />
          )}
          <AlertDialog
            open={openCancelConfirm}
            onOpenChange={setOpenCancelConform}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-center">
                  <LocalizedText id="legacy.are.you.sure.you.want.to.cancel.1r7wq8c" />
                </AlertDialogTitle>
                <AlertDialogDescription className="text-center">
                  <LocalizedText id="legacy.if.you.cancel.now.the.draft.brand.setup.1l8vgzt" />
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel type="button" className="cursor-pointer">
                  <LocalizedText id="legacy.go.back.1girtza" />
                </AlertDialogCancel>
                <AlertDialogAction
                  type="button"
                  onClick={() => {
                    void handleCancel();
                  }}
                  className="w-fit hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
                >
                  <LocalizedText id="legacy.cancel.setup.1s4t7l0" />
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </form>
      </FormProvider>
    </>
  );
};
